<template>
  <AnalyticsCard
    :title="tab.title"
    :subtitle="tab.subtitle"
    :loading="loading"
    :empty="!loading && rows.length === 0"
    :empty-title="tab.emptyTitle"
    :empty-hint="tab.emptyHint"
    skeleton="list"
    :rows="5"
    :height="264"
  >
    <template #actions>
      <div class="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
        <button v-for="option in TABS" :key="option.key" type="button" @click="active = option.key"
          class="px-2.5 py-1 text-[11px] font-semibold rounded-md transition-all"
          :class="active === option.key
            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'">
          {{ option.label }}
        </button>
      </div>
    </template>

    <RankedRows :rows="rows" />
  </AnalyticsCard>
</template>

<script setup>
import { ref, computed } from 'vue'
import AnalyticsCard from '../shared/AnalyticsCard.vue'
import RankedRows from '../shared/RankedRows.vue'
import { PALETTE } from '../shared/useAnalyticsChart.js'

const props = defineProps({
  landingPages: { type: Array, default: () => [] },
  campaigns:    { type: Array, default: () => [] },
  locales:      { type: Array, default: () => [] },
  loading:      { type: Boolean, default: false },
})

// Three small breakdowns that each deserve a place on the page but none of
// which deserves a full card. Tabs rather than three stacked panels, so the
// page does not end in a column of mostly empty boxes.
const TABS = [
  {
    key: 'pages', label: 'Pages',
    title: 'Landing pages', subtitle: 'The first page each visitor arrived on',
    emptyTitle: 'No landing pages yet',
    emptyHint: 'The page people arrive on tells you which link is actually circulating.',
  },
  {
    key: 'campaigns', label: 'Campaigns',
    title: 'Campaigns', subtitle: 'Visits carrying UTM tags',
    emptyTitle: 'No tagged campaigns',
    emptyHint: 'Add utm_source to a link you share and its traffic is tracked separately here.',
  },
  {
    key: 'locales', label: 'Languages',
    title: 'Visitor languages', subtitle: 'Browser language of your visitors',
    emptyTitle: 'No language data yet',
    emptyHint: 'Worth checking against the locales your booking pages offer.',
  },
]

// Both the constructor and the lookup can throw: the former where Intl.DisplayNames
// is unavailable, the latter on anything that is not a valid language tag, and
// this column takes whatever string the browser sent.
const LANGUAGE_NAMES = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'language' })
  } catch {
    return null
  }
})()

const active = ref('pages')
const tab = computed(() => TABS.find(t => t.key === active.value))

const rows = computed(() => {
  if (active.value === 'pages') {
    return props.landingPages.map(page => ({
      key: page.path, label: page.path, value: page.visitors, weight: page.visitors, swatch: PALETTE.blue,
    }))
  }

  if (active.value === 'campaigns') {
    return props.campaigns.map(c => ({
      key: `${c.source}-${c.medium}-${c.campaign}`,
      label: [c.source, c.campaign].filter(Boolean).join(' · '),
      value: c.visitors,
      weight: c.visitors,
      swatch: PALETTE.violet,
      meta: c.medium,
      badge: c.conversions > 0 ? `${Math.round(c.conversion_rate * 100)}% booked` : '',
      badgeClass: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400',
    }))
  }

  return props.locales.map(l => ({
    key: l.locale,
    label: languageName(l.locale),
    value: l.visitors,
    weight: l.visitors,
    swatch: PALETTE.cyan,
    meta: `${Math.round(l.share * 100)}%`,
  }))
})

function languageName(code) {
  try {
    return LANGUAGE_NAMES?.of(code) || code
  } catch {
    return code
  }
}
</script>
